import { Button, CircularProgress, Dialog, DialogActions, DialogContent } from "@mui/material";
import { Component } from "react";
import { MapContainer, Marker, TileLayer, useMap, useMapEvents, } from "react-leaflet";
import { createMarkerIcon } from "../utils";
import DialogTitleWithCloseButton from "./DialogTitleWithCloseButton";
import { Check, ContentPaste } from "@mui/icons-material";
import RoundedIconButton from "./RoundedIconButton";
import { errorToast } from "../toast";
import request from "../request";
import { getLatLongFromGoogleMapsURL } from "../../../shared/utils";

// helpers
const EventHandler = ({ onClick, onZoomChange }) => {
   useMapEvents({
      click(e) {
         const lat = e.latlng.lat;
         const long = e.latlng.lng;
         onClick({ lat, long })
      },
      zoomend(e) {
         const zoom =  e.target.getZoom();
         onZoomChange(zoom);
      }
   });
   return null;
};

function ChangeView({ center, zoom }) {
   const map = useMap();
   map.setView(center, zoom);
   return null;
}

const pinIcon = createMarkerIcon();

// component
export default class LocationPicker extends Component {

   state = {
      center: { lat: 0, long: 0 },
      zoom: 13,
      loading: false
   }

   getCurrentPosition() {
      return new Promise((resolve, reject) => {
         navigator.geolocation.getCurrentPosition(resolve, reject, { enableHighAccuracy: true, timeout: 10000 });
      });
   }

   setCenterToCurrentLocation = async () => {

      try {

         this.setState({ loading: true });

         const position = await this.getCurrentPosition();
         const lat = position.coords.latitude;
         const long = position.coords.longitude;

         this.setState({ center: { lat, long } });
         
      } catch (err) {
         errorToast('Failed to get current location');
      } finally {
         this.setState({ loading: false });
      }
   }

   pasteLocationLink = async () => {
      try {

         let url = await navigator.clipboard.readText();

         if (!url || (!url.includes('maps.app.goo.gl') && !url.includes('google.com/maps')))
            return errorToast("No location link in clipboard");

         this.setState({ loading: true });

         if (url.includes('maps.app.goo.gl')) {
            const res = await request.get('/api/general/resolved-url', { params: { url } });
            url = res.data.url;
         }

         const center = getLatLongFromGoogleMapsURL(url);
         this.setState({ center });

      } catch (err) {
         errorToast(err.message);
      } finally {
         this.setState({ loading: false });
      }
   }

   componentDidMount() {
      if (this.props.startPosition) {
         const center = this.props.startPosition;
         this.setState({ center })
      } else {
         this.setCenterToCurrentLocation();
      }
   }

   render() {

      const height = this.props.height || '100%';
      const { lat, long } = this.state.center;
      const { zoom, loading } = this.state;

      return <Dialog open fullScreen>
         <DialogTitleWithCloseButton onClose={this.props.onClose}>
            Pick location
         </DialogTitleWithCloseButton>
         <DialogContent className="p-0 relative">
            <MapContainer
               style={{ height }}
               center={[ lat, long ]}
               zoom={zoom}>
               <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
               />
               <Marker icon={pinIcon} position={[ lat, long ]} />
               <EventHandler
                  onClick={center => this.setState({ center })}
                  onZoomChange={zoom => this.setState({ zoom })}
               />
               <ChangeView zoom={zoom} center={[ lat, long ]} />
            </MapContainer>
            {
               loading && <div
                  className="absolute inset-0 flex justify-center items-center p-2 bg-[#000C] z-[5000]"
               >
                  <CircularProgress />
               </div>
            }
         </DialogContent>
         <DialogActions>
            <RoundedIconButton
               onClick={this.pasteLocationLink}
               size="small"
               color="tertiary"
               title="Paste location link"
               disabled={loading}
            >
               <ContentPaste fontSize="small" />
            </RoundedIconButton>
            <Button
               onClick={() => this.props.onClose(this.state.center)}
               className="rounded-full"
               variant="contained"
               size="small"
               startIcon={<Check />}
               disabled={loading}
            >
               Pick
            </Button>
         </DialogActions>
      </Dialog>
   }
}